import { Component, Output, EventEmitter, Input, AfterViewInit, inject, viewChild } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { FormGroup, FormControl } from '@angular/forms';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { IReceiptCodeQuery } from 'src/app/api/api-campaigns.service.generated';
import { SnackbarService } from 'src/app/common/services/snackbar-service';
import { merge} from 'rxjs';
import { map } from 'rxjs/operators';
import { DateUtils } from 'src/app/common/utils/DateUtils';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { ButtonComponent } from 'wecheer-common';

@Component({
    selector: 'app-receipt-code-filters',
    templateUrl: './receipt-code-filters.component.html',
    styleUrls: ['./receipt-code-filters.component.scss'],
    imports: [
      MatSidenavModule,
      MatIconModule,
      MatInputModule,
      MatFormFieldModule,
      MatDatepickerModule,
      NgxMatTimepickerModule,
      ReactiveFormsModule,
      ButtonComponent
    ]
})
export class ReceiptCodeFiltersComponent implements AfterViewInit {
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private snackBarService = inject(SnackbarService);


  @Input() searchTestReceipts: boolean;
  @Output() search = new EventEmitter<IReceiptCodeQuery>();
  readonly drawerFilters = viewChild<MatDrawer>('drawerFilters');

  form: FormGroup = new FormGroup({
    // scan date
    scanDateGreaterThan: new FormControl(''),
    scanDateLessThan: new FormControl(''),
    scanTimeGreaterThan: new FormControl(''),
    scanTimeLessThan: new FormControl(''),

    // receipt date
    receiptDateGreaterThan: new FormControl(''),
    receiptDateLessThan: new FormControl(''),
    receiptTimeGreaterThan: new FormControl(''),
    receiptTimeLessThan: new FormControl(''),

    invoiceNumber: new FormControl(''),
    customerCode: new FormControl(''),
    userId: new FormControl(''),
    campaignId: new FormControl(''),
    cardId: new FormControl(''),
    id: new FormControl(''),

  });

  filter: IReceiptCodeQuery;

  ngAfterViewInit() {

    this.activatedRoute.queryParams.subscribe(params => {
      this.setFormValuesFromQueryParams(params);
      this.filter = this.getFormValues();
      this.search.emit(this.filter);
      this.drawerFilters().close();
    });

    this.watchDatesAndResetTimeFields();
  }

  setFormValuesFromQueryParams(params: Params) {
    this.form.patchValue({
      ...params,
      scanDateGreaterThan: DateUtils.parseDate(params.scanDateGreaterThan),
      scanDateLessThan: DateUtils.parseDate(params.scanDateLessThan),
      receiptDateGreaterThan: DateUtils.parseDate(params.receiptDateGreaterThan),
      receiptDateLessThan: DateUtils.parseDate(params.receiptDateLessThan),
      invoiceNumber: params.invoiceNumber,
      customerCode: params.customerCode,
      userId: params.userId,
      campaignId: params.campaignId,
      cardId: params.cardId,
      id: params.id,
    });
  }
  getFormValues(): IReceiptCodeQuery {

    return {
      scanDateGreaterThan: this.getFormDateAndTime(this.form.value.scanDateGreaterThan, this.form.value.scanTimeGreaterThan, false),
      scanDateLessThan: this.getFormDateAndTime(this.form.value.scanDateLessThan, this.form.value.scanTimeLessThan, true),
      receiptDateGreaterThan: this.getFormDateAndTime(this.form.value.receiptDateGreaterThan, this.form.value.receiptTimeGreaterThan, false),
      receiptDateLessThan: this.getFormDateAndTime(this.form.value.receiptDateLessThan, this.form.value.receiptTimeLessThan, true),
      invoiceNumber: this.getFormValue(this.form.value.invoiceNumber),
      customerCode: this.getFormValue(this.form.value.customerCode),
      userId: this.getFormValue(this.form.value.userId),
      campaignId: this.getFormValue(this.form.value.campaignId),
      cardId: this.getFormValue(this.form.value.cardId),
      id: this.getFormValue(this.form.value.id),
    };
  }

  getFormDateAndTime(formDate: string, fromTime: string, endOfDay: boolean): Date {
    const date = formDate ? new Date(formDate) : undefined;
    DateUtils.setTime(date, fromTime, endOfDay);
    return date;
  }

  // Helper method to get undefined if the formValue is null (to be compatible with the automatically generated client class)
  getFormValue(formValue: any) {
    return formValue || undefined;
  }

  onReset() {
    this.form.reset({ maxProbability: 1.0, maxFocusScore: 1.0 });
    this.onSubmit();
  }

  onSubmit() {
    this.filter = this.getFormValues();

    // remove empty (undefined/null) properties from form values
    const queryParams =
      Object.keys(this.form.value)
        .filter(key => this.form.value.hasOwnProperty(key)) // convert to key value list
        .map(key => ({key: key, value: this.form.value[key]})) // skip empty fields
        .filter(kv => kv.value !== '' && kv.value !== null && kv.value !== undefined) // skip default form values
        .reduce((qParams: any, kv) => {
            qParams[kv.key] = kv.value;
            return qParams;
          },
          {}
        );

    this.router.navigate([], {
      queryParams: {
        ...queryParams,
        v: this.unique_id() // add random param value to create unique query string - to trigger queryParams change even if no param has changed
      }
    });
  }

  open() {
    this.drawerFilters().open();
  }

  encodeArray(arr: string[]): string {
    return arr && arr.length ? arr.join('----') : null;
  }
  decodeArray(arr: string): string[] {
    return arr ? arr.split('----') : null;
  }

  unique_id() {
    return 'xxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  watchDatesAndResetTimeFields() {
    const configs = [
      {
        dateFieldName: 'scanDateGreaterThan',
        timeFieldName: 'scanTimeGreaterThan',
        defaultTimeValue: '00:00'
      },
      {
        dateFieldName: 'scanDateLessThan',
        timeFieldName: 'scanTimeLessThan',
        defaultTimeValue: '23:59'
      },
      {
        dateFieldName: 'receiptDateGreaterThan',
        timeFieldName: 'receiptTimeGreaterThan',
        defaultTimeValue: '00:00'
      },
      {
        dateFieldName: 'receiptDateLessThan',
        timeFieldName: 'receiptTimeLessThan',
        defaultTimeValue: '23:59'
      },
    ];

    const observables = configs.map(config =>
      this.form.get(config.dateFieldName).valueChanges
        .pipe(map(dateFieldValue => {
          return {
            config,
            dateFieldValue
          };
      }))
    );

    merge(...observables).subscribe(({config, dateFieldValue}) => {
      const formValue: {[k: string]: any} = {};
      if (dateFieldValue) {
        // changed to a valid date
        // get time and if it is not set - set it to default
        const crtTime = this.form.value[config.timeFieldName];
        if (!crtTime) {
          formValue[config.timeFieldName] = config.defaultTimeValue;
          this.form.patchValue(formValue);
        }
      } else {
        formValue[config.timeFieldName] = null;
        // removed date filter - clear time also
        this.form.patchValue(formValue);
      }
    });
  }

  async onTimePickerTryOpen(isDisabled: boolean) {
    if (isDisabled) {
      this.snackBarService.open('Please select a date first!');
    }
  }
}
